import { HttpClient, HttpParams } from "@angular/common/http";
import { EventEmitter, inject, Injectable, Output } from "@angular/core";
import { Observable, ReplaySubject, throwError } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";

import { PayMethodDetail, PayMethodsPagination } from "@modules/employee-data/types/accounts";
import { environment } from "@environments/environment";
import {
	PayoutPagination,
	TransactionPagination,
	PayoutProcessingRoute,
	Payout,
	TransactionReportWithPagination,
	Transaction,
	PayoutStatusName
} from "../../models/payouts.types";
import { PaymentsApiService } from "@modules/dashboard/services/payments-api.service";

@Injectable({
	providedIn: "root"
})
export class PayoutsService {
	private reprocessIds$ = new ReplaySubject<Map<string, string[]>>();
	private employeePayMethodsCache = new Map<string, Observable<PayMethodsPagination>>();

	@Output() reloadPayoutData: EventEmitter<void> = new EventEmitter<void>();

	private http = inject(HttpClient);
	private paymentApiService = inject(PaymentsApiService);

	getPayoutsWithPagination(
		payGroupId: string,
		payCycleId: string,
		transactionStatusses: string[],
		pageSize: string,
		pageNumber: string
	): Observable<PayoutPagination> {
		return this.http.get<PayoutPagination>(
			`${environment.apiUri}/v1/payouts/nets?payGroupIds=${payGroupId}&payCycleIds=${payCycleId}&transactionStatuses=${transactionStatusses}&pageSize=${pageSize}&pageNumber=${pageNumber}&includeTransactions=true`
		);
	}

	getPayouts(payGroupId: string, payCycleId: string): Observable<PayoutPagination> {
		return this.http.get<PayoutPagination>(
			`${environment.apiUri}/v1/payouts/nets?payGroupIds=${payGroupId}&payCycleIds=${payCycleId}&pageSize=1000`
		);
	}

	getBookingState(payouts: any[], providerType = "API"): string {
		let hasCreated = 0;
		let hasProcessed = 0;
		let hasFailed = 0;
		let hasBooked = 0;
		let totalTransactions = -1;
		let hasCompleted = 0;
		let hasAllFailed = 0;

		if (payouts?.length > 0) {
			//Run through payout
			payouts.forEach(payout => {
				// Get their most recent tranactions
				payout.transactions = this.paymentApiService.filterTransactions(payout.transactions!);

				// Run through transactions -> Inactive payouts can't alter state of button
				if (payout.status.name.toString().toUpperCase() !== "INACTIVE") {
					payout.transactions.forEach((transaction: Transaction) => {
						if (totalTransactions === -1) {
							//Prevents that 'process' becomes the default
							totalTransactions++;
						}

						totalTransactions++;
						//Set booleans
						if (transaction.status.name.toString().toUpperCase() === "CREATED") {
							hasCreated++;
						} else if (transaction.status.name.toString().toUpperCase() === "PROCESSED") {
							hasProcessed++;
						} else if (
							transaction.status.name.toString().toUpperCase() === "FAILED" ||
							transaction.status.name.toString().toUpperCase() === "ATTEMPTS_LIMIT_EXCEEDED" ||
							transaction.status.name.toString().toUpperCase() === "ERROR"
						) {
							transaction.status.name.toString().toUpperCase() === "FAILED" && hasAllFailed++;
							hasFailed++;
						} else if (transaction.status.name.toString() === "BOOKED") {
							hasBooked++;
						} else if (transaction.status.name.toString() === "COMPLETED") {
							hasCompleted++;
						}
					});
				}
			});

			//Set button state based on totals

			if (hasCreated === totalTransactions) {
				return "process";
			} else if (hasProcessed === totalTransactions) {
				return providerType === "API" ? "inactive" : "completed";
			} else if (hasBooked === totalTransactions) {
				return providerType === "API" ? "cancel" : "complete-payments";
			} else if (hasAllFailed === totalTransactions) {
				return "cancel";
			} else if (hasFailed === totalTransactions) {
				return "cancel";
			} else if (hasProcessed + hasFailed === totalTransactions && hasBooked === 0) {
				return "inactive";
			} else if (hasBooked + hasFailed === totalTransactions && hasProcessed === 0) {
				return "cancel";
			} else if (hasCompleted === totalTransactions) {
				return "completed";
			} else {
				return "inactive";
			}
		}

		return "inactive";
	}

	getPayoutsForEmployeeInPayCycle(payGroupId: string, payCycleId: string, employeeId: string) {
		// A single employee having alot of payments in a single paycycle is unlikely hence 1000 is a safe number.
		return this.http.get<PayoutPagination>(
			`${environment.apiUri}/v1/payouts/nets?payGroupIds=${payGroupId}&payCycleIds=${payCycleId}&employeeIds=${employeeId}&pageSize=1000`
		);
	}

	getPayCycleStatusByIdAndGroup(payCycleId: string, type: string): Observable<any> {
		return this.http.get<PayoutPagination>(
			`${environment.apiUri}/v1/pay-cycles/${payCycleId}/statuses?milestoneGroup=${type}`
		);
	}

	getPaymentTypes(payCycleId: string, stage: string): Observable<string[]> {
		return this.http.get<string[]>(
			`${environment.apiUri}/v1/pay-cycles/${payCycleId}/payment-types?stage=${stage}`
		);
	}

	processPayments(
		payCycleId: string,
		payGroupId: string,
		triggerType: string,
		bankRoute: string,
		cardRoute: string,
		milestoneGroup = ""
	) {
		const route: PayoutProcessingRoute = {};

		if (bankRoute !== "" && bankRoute) {
			route["BANK"] = bankRoute;
		}
		if (cardRoute !== "" && cardRoute) {
			route["CARD"] = cardRoute;
		}

		const postParams: { route: PayoutProcessingRoute; triggerType?: string } = {
			route: route
		};

		if (triggerType !== "Book Payments") {
			postParams.triggerType = triggerType;
		}

		return this.http.post(
			`${environment.apiUri}/v1/pay-cycles/${payCycleId}/pay-groups/${payGroupId}/milestone-group/${milestoneGroup}/activation`,
			postParams
		);
	}

	/** New endpoint call for NON API provider to change status from BOOKED to PROCESSED/COMPLETED */
	completePayments(payCycleId: string, payGroupId: string, milestoneGroup = ""): Observable<any> {
		const postParams = {
			payCycleId,
			payGroupId,
			milestoneGroup
		};

		return this.http.post(`${environment.apiUri}/v1/payouts/transactions/non-api/complete`, postParams);
	}

	reProcessPayments(
		triggerType: string,
		bankRoute: string,
		cardRoute: string,
		reProcesMapPayoutsAndTxId: Map<string, string[]>
	) {
		const route: PayoutProcessingRoute = {};

		if (bankRoute !== "" && bankRoute) {
			route["BANK"] = bankRoute;
		}
		if (cardRoute !== "" && cardRoute) {
			route["CARD"] = cardRoute;
		}

		return this.http.post(`${environment.apiUri}/v2/payouts/nets/reprocess`, {
			ids: this.getTransactionIdsFormMapPayouts(reProcesMapPayoutsAndTxId),
			triggerType: triggerType,
			route: route
		});
	}

	/**
	 * Endpoint call for bulk inactivate payouts
	 * @param payoutIds: payout ids
	 */
	bulkInactivatePayouts(payoutIds: string[]): Observable<any> {
		const inactivateParams = {
			payoutIds
		};

		return this.http.post(`${environment.apiUri}/v1/payouts/nets/inactivate`, inactivateParams);
	}

	/**
	 * Endpoint call for mark as Booked/Processed transactions manually
	 * @param status
	 * @param transactionIds
	 * @param message
	 * @returns marked booked/processed empty when no failed transactions
	 */
	markAsBookedOrProcessed(status: string, transactionIds: string[], message: string): Observable<any> {
		const markManualParams = {
			transactionIds,
			message
		};

		return this.http.put(`${environment.apiUri}/v1/payouts/transactions/status/${status}`, markManualParams);
	}

	getTransactionIdsFormMapPayouts(reProcesMapPayoutsAndTxId: Map<string, string[]>): string[] {
		let transactionsIds: string[] = [];
		reProcesMapPayoutsAndTxId.forEach((value: string[]) => {
			if (value.length > 0) transactionsIds = transactionsIds.concat(value);
		});
		return transactionsIds;
	}

	inactiveNetPayout(payCycleId: string, employeeId: string) {
		return this.http.post(
			`${environment.apiUri}/v1/payouts/nets/pay-cycles/${payCycleId}/employee/${employeeId}/inactivate`,
			null
		);
	}

	searchPayoutsTransactions(payoutId: string): Observable<TransactionPagination> {
		let params = new HttpParams();

		params = params.append("payoutIds", payoutId);
		params = params.append("pageSize", "100");
		params = params.append("pageNumber", "0");

		return this.http.get<TransactionPagination>(`${environment.apiUri}/v1/payouts/nets/transactions`, { params });
	}

	getTransactionHistory(
		payoutId: string,
		paymethodId: string,
		rangeStart: string,
		rangeEnd: string
	): Observable<TransactionReportWithPagination> {
		return this.http.get<TransactionReportWithPagination>(
			`${environment.apiUri}/v1/transactions/report?rangeStart=${rangeStart}&rangeEnd=${rangeEnd}&payoutIds=${payoutId}&payMethodIds=${paymethodId}`
		);
	}

	getEmployeePaymentMethods(employeeId: string): Observable<PayMethodDetail[]> {
		return this.getPaymentMethods(employeeId).pipe(
			map(res => {
				return res.items;
			})
		);
	}

	getPaymentMethods(employeeId: string): Observable<PayMethodsPagination> {
		const cacheKey = employeeId.trim();
		const cachedPayMethods = this.employeePayMethodsCache.get(cacheKey);

		if (cachedPayMethods) {
			return cachedPayMethods;
		}

		const payMethods$ = this.http
			.get<PayMethodsPagination>(
				`${environment.apiUri}/v1/employees/${cacheKey}/pay-methods/details?payMethodTypes=CARD,BANK&pageSize=1000`
			)
			.pipe(
				shareReplay({ bufferSize: 1, refCount: false }),
				catchError(error => {
					this.employeePayMethodsCache.delete(cacheKey);
					return throwError(() => error);
				})
			);

		this.employeePayMethodsCache.set(cacheKey, payMethods$);
		return payMethods$;
	}

	reprocessIdsObservable(): Observable<Map<string, string[]>> {
		return this.reprocessIds$.asObservable();
	}

	reload() {
		this.employeePayMethodsCache.clear();
		this.reloadPayoutData.emit();
	}

	updateReprocessIds(reProcesMapPayoutsAndTxId: Map<string, string[]>): void {
		this.reprocessIds$.next(reProcesMapPayoutsAndTxId);
	}

	selectAllReprocessIds(allTransactions: Transaction[]): void {
		const reprocessIds: Map<string, string[]> = new Map<string, string[]>();

		allTransactions.forEach(transaction => {
			if (reprocessIds.has(transaction.payoutId)) {
				// If the key exists, add the transaction.id to the array
				reprocessIds.get(transaction.payoutId)!.push(transaction.id);
			} else {
				// If the key does not exist, create a new array with the transaction.id
				reprocessIds.set(transaction.payoutId, [transaction.id]);
			}
		});

		this.reprocessIds$.next(reprocessIds);
	}

	clearReprocessIds(): void {
		this.reprocessIds$.next(new Map<string, string[]>());
	}

	getPayoutStatus(payout: Payout, transactions: Transaction[]): PayoutStatusName {
		if (payout.transactions && payout.transactions.length > 0 && payout.status.name.toLowerCase() !== "inactive") {
			transactions = this.paymentApiService.filterTransactions(transactions);
			const transactionStatusses: string[] = [];
			transactions.forEach(transaction => transactionStatusses.push(transaction.status.name));
			return this.paymentApiService.getMostPessimisticStatus(transactionStatusses, payout.status.name);
		} else {
			return payout.status.name;
		}
	}

	searchPayoutsDeductions(employeeIds: string[], payCycleIds: string[]): Observable<TransactionPagination> {
		return this.http.post<TransactionPagination>(`${environment.apiUri}/v1/deductions/search`, {
			pageNumber: 0,
			pageSize: 10,
			payCycleIds,
			employeeIds
		});
	}
}
